import { useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createResponse, type BodyType } from '@/api/responses'
import { createScenario } from '@/api/scenarios'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ── Schema ─────────────────────────────────────────────────────────────────

const BODY_TYPES: BodyType[] = ['json', 'text', 'xml', 'binary_base64']

type MatchOperator = 'exact' | '>=' | '<=' | '>' | '<'

const stepSchema = z.object({
  match_operator: z.enum(['exact', '>=', '<=', '>', '<'] as const).default('exact'),
  match_value: z.coerce.number().int().min(1),
  status: z.coerce.number().int().min(100).max(599).default(200),
  body_type: z.enum(['json', 'text', 'xml', 'binary_base64'] as const).default('json'),
  body: z.string().default(''),
  headersJson: z
    .string()
    .default('{}')
    .refine((s) => { try { JSON.parse(s); return true } catch { return false } }),
  delay_ms: z.coerce.number().int().min(0).max(30000).default(0),
})

type ScopeType = 'global' | 'per_ip' | 'per_header'

const schema = z.object({
  scenarioName: z.string().min(1).max(100),
  priority: z.coerce.number().int().default(0),
  enabled: z.boolean().default(true),
  scope: z.enum(['global', 'per_ip', 'per_header'] as const).default('global'),
  per_header_name: z.string().optional(),
  reset_after_seconds: z.union([z.coerce.number().int().positive(), z.literal('')]).optional(),
  steps: z.array(stepSchema).min(1),
})

type FormValues = z.infer<typeof schema>

// ── Helpers ────────────────────────────────────────────────────────────────

function buildMatchCount(op: MatchOperator, val: number): number | string {
  return op === 'exact' ? val : `${op}${val}`
}

function buildScope(scope: ScopeType, headerName?: string): string {
  if (scope === 'per_header') return `per_header:${headerName ?? 'x-user-id'}`
  return scope
}

// ── Step card ──────────────────────────────────────────────────────────────

interface StepCardProps {
  index: number
  total: number
  control: ReturnType<typeof useForm<FormValues>>['control']
  register: ReturnType<typeof useForm<FormValues>>['register']
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors']
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

function StepCard({ index, total, control, register, errors, onRemove, onMoveUp, onMoveDown }: StepCardProps) {
  const stepErrors = errors.steps?.[index]
  const { t } = useTranslation()

  return (
    <div className="rounded-md border bg-muted/20 p-4 space-y-3">
      {/* Step header */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{t('scenarioForm.step')} {index + 1}</span>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveUp} disabled={index === 0}>
            <ChevronUp className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveDown} disabled={index === total - 1}>
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onRemove} disabled={total <= 1}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* match_count */}
      <div className="grid grid-cols-3 gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.condition')}</Label>
          <Controller
            name={`steps.${index}.match_operator`}
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">{t('scenarioForm.conditionExact')}</SelectItem>
                  <SelectItem value=">=">{t('scenarioForm.conditionGte')}</SelectItem>
                  <SelectItem value="<=">{t('scenarioForm.conditionLte')}</SelectItem>
                  <SelectItem value=">">&gt;</SelectItem>
                  <SelectItem value="<">&lt;</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.requestCount')}</Label>
          <Input className="h-9" type="number" min={1} {...register(`steps.${index}.match_value`)} />
        </div>
        <div className="text-xs text-muted-foreground pb-2">
          {t('scenarioForm.matchesSuffix')}
        </div>
      </div>
      {stepErrors?.match_value && (
        <p className="text-sm text-destructive">{t('scenarioForm.matchValueMin')}</p>
      )}

      {/* Response inline */}
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.statusCode')}</Label>
          <Input className="h-9" type="number" {...register(`steps.${index}.status`)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.bodyType')}</Label>
          <Controller
            name={`steps.${index}.body_type`}
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BODY_TYPES.map((bt) => (
                    <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.delayMs')}</Label>
          <Input className="h-9" type="number" {...register(`steps.${index}.delay_ms`)} />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('scenarioForm.body')}</Label>
        <Textarea
          rows={3}
          className={cn('font-mono text-sm')}
          placeholder='{"message": "ok"}'
          {...register(`steps.${index}.body`)}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('scenarioForm.headers')}</Label>
        <Textarea
          rows={2}
          className="font-mono text-sm"
          placeholder='{}'
          {...register(`steps.${index}.headersJson`)}
        />
        {stepErrors?.headersJson && (
          <p className="text-sm text-destructive">{t('scenarioForm.jsonFormatError')}</p>
        )}
      </div>
    </div>
  )
}

// ── Default step ───────────────────────────────────────────────────────────

function defaultStep(matchValue: number) {
  return {
    match_operator: 'exact' as const,
    match_value: matchValue,
    status: 200,
    body_type: 'json' as const,
    body: '{\n  \n}',
    headersJson: '{}',
    delay_ms: 0,
  }
}

// ── Component ──────────────────────────────────────────────────────────────

interface SequenceScenarioFormProps {
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

export function SequenceScenarioForm({ projectId, endpointId, onSuccess, onCancel }: SequenceScenarioFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { register, handleSubmit, control, watch, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        scenarioName: '',
        priority: 0,
        enabled: true,
        scope: 'global',
        per_header_name: '',
        reset_after_seconds: '',
        steps: [defaultStep(1), defaultStep(2)],
      },
    })

  const { fields, append, remove, swap } = useFieldArray({ control, name: 'steps' })

  const scope = watch('scope')

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const responses = await Promise.all(
        data.steps.map((step) => {
          const headers = JSON.parse(step.headersJson) as Record<string, string>
          return createResponse(projectId, {
            status: step.status,
            body_type: step.body_type,
            body: step.body || undefined,
            headers,
            delay_ms: step.delay_ms,
          })
        })
      )

      const steps = data.steps.map((step, i) => ({
        id: crypto.randomUUID(),
        match_count: buildMatchCount(step.match_operator as MatchOperator, step.match_value),
        response_id: responses[i].id,
      }))

      const scopeStr = buildScope(data.scope, data.per_header_name)
      const reset = data.reset_after_seconds !== '' && data.reset_after_seconds !== undefined
        ? Number(data.reset_after_seconds)
        : undefined

      await createScenario(endpointId, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        type: 'sequence',
        config: { steps, scope: scopeStr, reset_after_seconds: reset },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('scenarioForm.createFailed'))
    },
  })

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {serverError}
        </p>
      )}

      {/* ── Scenario settings ─────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="scenarioName">{t('scenarioForm.scenarioName')}</Label>
            <Input id="scenarioName" placeholder={t('scenarioForm.scenarioNamePlaceholder')} {...register('scenarioName')} />
            {errors.scenarioName && (
              <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="priority">{t('scenarioForm.priority')}</Label>
            <Input id="priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
              )}
            />
          </div>
        </div>
      </div>

      {/* ── Sequence settings ────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.sequenceSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t('scenarioForm.scope')}</Label>
            <Controller
              name="scope"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">{t('scenarioForm.scopeGlobal')}</SelectItem>
                    <SelectItem value="per_ip">{t('scenarioForm.scopePerIp')}</SelectItem>
                    <SelectItem value="per_header">{t('scenarioForm.scopePerHeader')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          {scope === 'per_header' && (
            <div className="space-y-1.5">
              <Label htmlFor="per_header_name">{t('scenarioForm.headerName')}</Label>
              <Input id="per_header_name" placeholder="x-user-id" {...register('per_header_name')} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="reset_after_seconds">{t('scenarioForm.resetSeconds')}</Label>
            <Input id="reset_after_seconds" type="number" min={1} placeholder={t('scenarioForm.resetSecondsPlaceholder')} {...register('reset_after_seconds')} />
          </div>
        </div>
      </div>

      {/* ── Steps ────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {t('scenarioForm.steps')} ({fields.length})
        </h4>
        {errors.steps?.root && (
          <p className="text-sm text-destructive">{t('scenarioForm.stepMinOne')}</p>
        )}
        <div className="space-y-3">
          {fields.map((field, i) => (
            <StepCard
              key={field.id}
              index={i}
              total={fields.length}
              control={control}
              register={register}
              errors={errors}
              onRemove={() => remove(i)}
              onMoveUp={() => swap(i, i - 1)}
              onMoveDown={() => swap(i, i + 1)}
            />
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append(defaultStep(fields.length + 1))}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('scenarioForm.addStep')}
        </Button>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? t('common.creating') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
